import { useState } from "react"
import { Link, useParams } from "react-router-dom"
import { describeError } from "../api/http"
import {
	membershipOf,
	useGroup,
	useGroupRows,
	usePriceGroups,
} from "../api/queries"
import { useGroupRights } from "../auth/rights"
import { Note } from "../components/Note"
import OnSiteTag from "../components/site/OnSiteTag"
import { useOnSite } from "../components/site/useOnSite"
import { usePublishTarget } from "../components/site/usePublishTarget"
import { useLanguageId } from "../lib/language"
import DescriptionTab from "./group/DescriptionTab"
import SiteTab from "./group/SiteTab"
import TablePart from "./group/TablePart"
import ToolsetComposition from "./group/ToolsetComposition"

/**
 * Две вкладки вместо прежних четырёх.
 *
 * «Описание» и «Табличная часть» — это одна работа над карточкой, их развели
 * только потому, что так было в ECM; теперь они идут одной страницей.
 * «Категории» уехали в «Сайт»: категории и есть дерево сайта, и вкладка сайта
 * всё равно их показывала.
 */
const TABS = [
	{ id: "card", label: "Карточка" },
	{ id: "site", label: "Сайт" },
] as const

type Tab = (typeof TABS)[number]["id"]

/** Карточка группы: шапка с признаками и две вкладки. Страницы отдельного
 *  товара в рижском интерфейсе нет — товары ведутся здесь же. */
export default function GroupScreen() {
	const { id } = useParams<{ id: string }>()
	const languageId = useLanguageId()

	const group = useGroup(id, languageId)
	const rows = useGroupRows(id, languageId)
	const priceGroups = usePriceGroups()

	const [tab, setTab] = useState<Tab>("card")

	const data = group.data
	const rights = useGroupRights(data?.priceGroupId ?? null, languageId)

	// Сайт тот же, что выбран на `/publish`. Состояние «на сайте» лежит в общем
	// кэше запроса, поэтому метка в шапке и вкладка «Сайт» показывают одно и то
	// же и лишнего запроса в базу сайта не делают.
	const { sites, site, siteCatalog, selectSite } = usePublishTarget()
	const onSite = useOnSite(id ?? null, site?.id ?? null, rows.data?.length)

	// хуки — до ранних возвратов: порядок хуков не должен меняться между рендерами
	if (group.isLoading) {
		return (
			<div className="state">
				<span className="spinner" />
			</div>
		)
	}

	if (group.isError || !data) {
		return (
			<div className="state">
				<Note tone="error">
					Не удалось открыть группу: {describeError(group.error)}
				</Note>
				<Link to="/groups">← к списку групп</Link>
			</div>
		)
	}

	const membership = membershipOf(data.priceGroupId)
	const priceGroupName =
		priceGroups.data?.find(item => item.id === data.priceGroupId)?.name ??
		`#${data.priceGroupId}`

	return (
		<>
			<div className="page-head">
				<Link to="/groups" className="btn" title="К списку групп">
					←
				</Link>
				<div className="page-head__text">
					<h1>{data.name || "Без названия"}</h1>
					<div className="page-head__meta">
						<span className="mono">{data.id}</span>
						<span
							className={
								"tag " + (membership === "own" ? "tag--own" : "tag--foreign")
							}
						>
							{membership === "own" ? "Рига" : priceGroupName}
						</span>
						{data.isToolset && <span className="tag tag--muted">набор</span>}
						{site && <OnSiteTag state={onSite} siteName={site.name} />}
						<span>
							фото {data.isImageChecked ? "проверены" : "не проверены"} ·
							описание {data.isDescriptionChecked ? "проверено" : "не проверено"}
						</span>
					</div>
				</div>
			</div>

			<div className="tabs">
				{TABS.map(item => (
					<button
						key={item.id}
						aria-selected={tab === item.id}
						onClick={() => setTab(item.id)}
					>
						{item.label}
					</button>
				))}
			</div>

			{membership === "foreign" && (
				<Note tone="hint">
					<b>Группа РФ.</b> Доступны название на нерусских языках, главный
					атрибут карточки и категории на сайте. Остальное ведёт российская
					сторона.
				</Note>
			)}

			{tab === "card" && (
				<>
					<DescriptionTab group={data} languageId={languageId} rights={rights} />

					<div className="group-section">
						Табличная часть
						{rows.data ? <span className="muted"> · {rows.data.length}</span> : null}
					</div>
					<TablePart group={data} languageId={languageId} rights={rights} />

					{data.isToolset &&
						(rows.data && rows.data.length > 0 ? (
							<ToolsetComposition
								groupId={data.id}
								toolsetId={rows.data[0].id}
								toolsetName={rows.data[0].name}
								languageId={languageId}
								rights={rights}
							/>
						) : rows.data ? (
							<Note tone="hint">
								Состав набора появится, когда в карточке будет товар — сам набор.
								Добавьте его выше.
							</Note>
						) : null)}
				</>
			)}

			{tab === "site" && (
				<SiteTab
					group={data}
					languageId={languageId}
					rights={rights}
					isForeign={membership === "foreign"}
					productCount={rows.data?.length}
					sites={sites}
					site={site}
					siteCatalog={siteCatalog}
					onSite={selectSite}
				/>
			)}
		</>
	)
}
